import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { type DocNode } from '@atlaskit/adf-schema';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import {
	type ExperienceTrackerAPI,
	useAIMateExperienceTracker,
} from '@atlassian/conversation-assistant-instrumentation/main';
import { type AgentAction } from '@atlassian/conversation-assistant-service-api/types/agent-actions';
import {
	type Agent,
	type AgentDetails,
	type AgentKnowledgeConfigurationResponse,
	type AgentPermissionResponse,
	type AssistanceService,
	type AssistanceServiceConfig,
	type AssistanceServiceProduct,
	type Chat,
	type ChatMessage,
	type ConversationChannel,
	type ConversationContent,
	type ConversationStarterResponse,
	type CreatePromptRequest,
	type Dialogues,
	type DynamicConversationStarterRequestNew,
	type DynamicConversationStarterResponseNew,
	type FetchStreamReturn,
	type FileUploadResponse,
	type FileValidationPayload,
	type FileValidationResponse,
	type GetAgentPermissionsPayload,
	type GetAllPromptsResponse,
	type GetPromptResponse,
	isHumanMessage,
	type NLCreateAgentPayload,
	type NLCreateAgentStreamMessage,
	type PostAgentPayload,
	type RawChat,
	type RovoLabsChatStreamPayload,
	type RovoLabsConfirmedTools,
	type RovoLabsDefaultPrompt,
	type RovoLabsLanguageModel,
	type RovoLabsTool,
	type RovoUserSettings,
	type SendMessageBrowserContext,
	type SendMessageDashboardContext,
	type SendMessageDatabaseContext,
	type SendMessageEditorContext,
	type SendMessageWhiteboardContext,
	type SendMessageWorkflowContext,
	type StreamMessage,
	type WebSearchConfigurationResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type MessageActionPayload,
	type MessageActionResponse,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { getTraceIdFromResponse } from '@atlassian/conversation-assistant-utils/httpUtils';

import { setRovoChatProduct } from '../../common/utils/product-config';

import { readJsonStream } from './read-json-stream';
import { readSSEStream } from './read-sse-stream';
import { addPath, processResponse } from './utils/client';
import { generateConversationName } from './utils/conversation';
import { toStreamResponse } from './utils/data';

type FetchOptions = {
	path: string;
	options: RequestInit;
	holdName?: string;
};

const PRODUCT_HEADER = 'x-product';
const EXPERIENCE_ID_HEADER = 'x-experience-id';
const CHANNEL_ID_HEADER = 'X-Channel-Id';

type PrivateAssistanceServiceConfig = AssistanceServiceConfig &
	Required<Pick<AssistanceServiceConfig, 'baseUrl' | 'headers'>>;

export abstract class AssistanceServiceBase {
	private config: PrivateAssistanceServiceConfig;

	public getConfig = () => {
		return {
			...this.config,
		};
	};

	constructor(props: AssistanceServiceConfig) {
		const defaultBaseUrl = '/gateway/api/assist';

		const defaultConfig = {
			baseUrl: defaultBaseUrl,
			headers: {},
		};

		this.config = {
			...props,
			baseUrl: props.baseUrl || defaultConfig.baseUrl,
			credentials: props.credentials,
			headers: {
				...(props.headers || defaultConfig.headers),
			},
		};
	}

	protected createHeaders(init?: HeadersInit): Headers {
		return new Headers({
			[PRODUCT_HEADER]: this.config.product,
			[EXPERIENCE_ID_HEADER]: this.config.experienceId,
			...(this.config.headers || {}),
			...(init || {}),
		});
	}

	protected async fetch({ path, options, holdName }: FetchOptions) {
		const { baseUrl: configBaseUrl, credentials: configCredentials } = this.config;
		const baseUrl = configBaseUrl;
		const requestUrl = addPath(baseUrl, path);

		const startTime = performance.now();
		const response = await window.fetch(requestUrl, {
			credentials: configCredentials,
			...options,
		});

		if (this.config.labelStack) {
			// Please note that the recorded duration of this hold may differ from the time spent actually fetching the data.
			// This is because the browser may schedule network calls and JavaScript execution at its own discrection based
			// on available resources, the main thread being blocked, etc... Still, it is a helpful approximation.
			const endTime = performance.now();
			addSpanToAll(
				'fetch',
				holdName ? `${options.method}-${holdName}` : 'unknown',
				this.config.labelStack,
				startTime,
				endTime,
			);
		}

		return processResponse({ response });
	}

	protected async fetchJson<T>(options: FetchOptions): Promise<T> {
		const response = await this.fetch(options);
		const json = await response.json();
		return json;
	}

	// Overload: useSSE false or not provided - yields T only (JSON stream)
	protected async fetchStream<T>(
		options: FetchOptions,
		config?: { useSSE?: false },
	): Promise<FetchStreamReturn<T, false>>;

	// Overload: useSSE true - yields ParsedSSEMessage<T> or T (JSON fallback)
	protected async fetchStream<T>(
		options: FetchOptions,
		config: { useSSE: true },
	): Promise<FetchStreamReturn<T, true>>;

	// Overload: useSSE boolean (dynamic) - yields union type for type safety
	protected async fetchStream<T>(
		options: FetchOptions,
		config: { useSSE?: boolean },
	): Promise<FetchStreamReturn<T, boolean>>;

	protected async fetchStream<T>(
		options: FetchOptions,
		config?: { useSSE?: boolean },
	): Promise<FetchStreamReturn<T, boolean>> {
		const response = await this.fetch(options);
		const contentType = response.headers.get('content-type') ?? '';
		const headerSuggestsSSE = config?.useSSE && contentType.includes('text/event-stream');

		return {
			responseData: {
				traceId: getTraceIdFromResponse(response),
			},
			generator: headerSuggestsSSE ? readSSEStream<T>(response) : readJsonStream<T>(response),
		};
	}
}

const getHref = (product?: string) => {
	const backendOriginHost = (window as any)?.__backendOriginHost;
	if (
		product === 'confluence' &&
		window.location.hostname === 'localhost' &&
		typeof backendOriginHost === 'string'
	) {
		const urlWithLocalhost = new URL(window?.location?.href);
		urlWithLocalhost.hostname = backendOriginHost;
		urlWithLocalhost.port = '';
		urlWithLocalhost.protocol = 'https:';
		return urlWithLocalhost.toString();
	}
	return window?.location?.href;
};

export class AssistanceServiceImpl extends AssistanceServiceBase implements AssistanceService {
	constructor(props: AssistanceServiceConfig) {
		super(props);
	}

	public async sendMessageStream({
		agentNamedId,
		agentId,
		message,
		conversationId,
		controller,
		storeMessage = false,
		citationsEnabled = true,
		aiFeatureContext,
		jiraWorkflowContext,
		editorContext,
		whiteboardContext,
		databaseContext,
		browserContext,
		additionalContext,
		dashboardContext,
		fileIds,
		webSearchEnabled,
		deepResearchEnabled,
		thinkDeeperEnabled,
		appFilters,
		useCurrentBrowsingContext = undefined,
		minionAliasConfig,
		optInToDeepResearchV2 = false,
		optInThinkDeeperMessages = false,
		browserContextSanitizer,
		optInSSEStream = false,
	}: {
		conversationId: string;
		message: string | DocNode;
		agentNamedId?: string;
		agentId?: string;
		controller?: AbortController;
		storeMessage?: boolean;
		citationsEnabled?: boolean;
		aiFeatureContext?: Record<string, unknown>;
		jiraWorkflowContext?: SendMessageWorkflowContext;
		editorContext?: SendMessageEditorContext;
		whiteboardContext?: SendMessageWhiteboardContext;
		databaseContext?: SendMessageDatabaseContext;
		browserContext?: SendMessageBrowserContext;
		additionalContext?: Record<string, unknown>;
		dashboardContext?: SendMessageDashboardContext;
		fileIds?: string[];
		webSearchEnabled?: boolean;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		appFilters?: string[];
		useCurrentBrowsingContext?: boolean;
		minionAliasConfig?: Record<string, unknown>;
		optInToDeepResearchV2?: boolean;
		optInThinkDeeperMessages?: boolean;
		browserContextSanitizer?: {
			isBlocklisted: (url: string) => boolean;
		};
		optInSSEStream?: boolean;
	}): Promise<FetchStreamReturn<StreamMessage, boolean>> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
			[CHANNEL_ID_HEADER]: 'rovo-chat',
			// This header is used to opt-in to Deep Research v2 on the BE.
			// This is to ensure that we don't use v2 deep research when we are using the old Rovo Chat UI prior to the refresh..
			...(optInToDeepResearchV2 && { 'X-Deep-Research-V2-Opt-In': 'true' }),
			// Opt in to using RESEARCH_STEP, RESEARCH_PLAN, and similar messages for "think deeper".
			// Ensures backward compatibility with legacy clients and allows application targeting within the experiment.
			...(optInThinkDeeperMessages && { 'X-Think-Deeper-Messages-Opt-In': 'true' }),
		});
		let knowledgeOverride = {};
		// not setting knowledgeEnabled and filters means all org knowledge is applied by default.
		if (appFilters) {
			if (appFilters.length === 0) {
				knowledgeOverride = {
					knowledgeEnabled: false,
				};
			} else {
				knowledgeOverride = {
					knowledgeEnabled: true,
					filters: {
						knowledgeSources: appFilters,
					},
				};
			}
		}
		// Check if the URL is blocklisted
		const browserUrl = browserContext?.context?.browserUrl;
		const isBlocklisted =
			browserUrl && browserContextSanitizer
				? browserContextSanitizer.isBlocklisted(browserUrl)
				: false;

		// Determine the URL to send to the backend
		// If blocklisted, send empty URL to ensure the site is not sent to the backend
		// If browserContext received and context is undefined, assume we're unable to retrieve the context of the page in focus
		let url: string;
		if (
			isBlocklisted ||
			(browserContext?.context?.browserUrl === 'NO-URL' &&
				FeatureGates.getExperimentValue('jira_free_gen_editor', 'isEnabled', false))
		) {
			url = '';
		} else if (browserContext?.context?.browserUrl) {
			url = browserContext.context.browserUrl;
		} else {
			url = getHref(this.getConfig().product) ?? '';
		}
		const body = {
			content: message,
			context: {
				// without useCurrentBrowsingContext browser_url is not always used by the Rovo,
				// instead it depends on prompt intent to use it,
				// with useCurrentBrowsingContext we can ensure it is always used
				browser_url: url,
				...(useCurrentBrowsingContext === undefined
					? {}
					: { useCurrentBrowsingContext: useCurrentBrowsingContext }),
				webSearchEnabled,
				deepResearchEnabled,
				...(thinkDeeperEnabled ? { extendedThinkingEnabled: thinkDeeperEnabled } : {}),
				...(editorContext ? { editor: editorContext } : {}),
				...(whiteboardContext ? { whiteboardContext } : {}),
				...(databaseContext ? { databaseContext } : {}),
				// If browserUrl is inaccessable, fallback to htmlBody or canvasText if they exist
				// Do not send htmlBody or canvasText if the URL is blocklisted to ensure the site is truly not sent to the backend
				...(browserContext && !isBlocklisted
					? {
							browser: {
								htmlBody: browserContext.context?.htmlBody,
								canvasText: browserContext.context?.canvasText,
							},
						}
					: {}),
				jiraWorkflowContext,
				...additionalContext,
				dashboardContext,
				aiFeatureContext,
				fileIds,
				...knowledgeOverride,
			},
			recipient_agent_named_id: agentNamedId,
			agent_id: agentId,
			mimeType: typeof message === 'string' ? 'text/markdown' : 'text/adf',
			// don't allow store message if feature flag is off
			store_message: fg('ai_mate_show_store_messages') ? storeMessage : false,
			citations_enabled: citationsEnabled,
			...(minionAliasConfig ??
				(this.getConfig().minionAlias
					? { stratus_config: { minion_alias: this.getConfig().minionAlias } }
					: {})),
		};
		const options: RequestInit = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(body),
			signal: controller?.signal,
		};

		return this.fetchStream<StreamMessage>(
			{
				path: optInSSEStream
					? `/rovo/v1/chat/conversation/${conversationId}/message/stream/sse`
					: `/rovo/v1/chat/conversation/${conversationId}/message/stream`,
				options,
			},
			{ useSSE: optInSSEStream },
		);
	}

	public async reconnectToMessageStream({
		conversationId,
		messageId,
		lastEventId,
		controller,
	}: {
		conversationId: string;
		messageId: string;
		lastEventId?: string;
		controller?: AbortController;
	}): Promise<FetchStreamReturn<StreamMessage, true>> {
		const headers = this.createHeaders();

		// Add Last-Event-ID header if available
		if (lastEventId) {
			headers.set('Last-Event-ID', lastEventId);
		}

		const options: RequestInit = {
			method: 'GET',
			headers,
			signal: controller?.signal,
		};

		return this.fetchStream<StreamMessage>(
			{
				path: `/rovo/v1/chat/conversation/${conversationId}/message/${messageId}/stream/sse`,
				options,
			},
			{ useSSE: true },
		);
	}

	public async sendMessageConfirmedTools({
		conversationId,
		agentId,
		controller,
		confirmed_tools,
		optInSSEStream = false,
	}: {
		conversationId: string;
		agentId?: string;
		controller?: AbortController;
		confirmed_tools: RovoLabsConfirmedTools;
		optInSSEStream?: boolean;
	}): Promise<FetchStreamReturn<StreamMessage>> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const url = window?.location?.href;

		const body = {
			conversation_id: conversationId,
			agent_id: agentId,
			confirmed_tools,

			context: {
				browser_url: url,
			},

			// These values are hardcoded because they are not used in backend, but required in the contract
			content: '',
			mimeType: 'text/markdown',
			store_message: false,
			citations_enabled: false,
		};
		const options: RequestInit = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(body),
			signal: controller?.signal,
		};

		return this.fetchStream<StreamMessage>(
			{
				path: optInSSEStream
					? `/rovo/v1/chat/conversation/${conversationId}/message/stream/sse`
					: `/rovo/v1/chat/conversation/${conversationId}/message/stream`,
				options,
			},
			{ useSSE: optInSSEStream },
		);
	}

	public async nlCreateAgentStream({
		controller,
		latest_user_message,
		draft_agent,
		conversation_history,
	}: {
		controller?: AbortController;
	} & NLCreateAgentPayload): Promise<FetchStreamReturn<NLCreateAgentStreamMessage>> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const body = {
			latest_user_message,
			draft_agent,
			conversation_history,
		};
		const options: RequestInit = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(body),
			signal: controller?.signal,
		};

		return this.fetchStream<NLCreateAgentStreamMessage>({
			path: `/rovo/v1/agents/nlcreate`,
			options,
		});
	}

	public async getChat(conversationId: string): Promise<Chat> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers: headers,
		};

		const rawChat = await this.fetchJson<{
			items: RawChat;
		}>({
			path: `/rovo/v1/chat/conversation/${conversationId}/messages`,
			options,
			holdName: 'rovo-v1-chat-conversation-[conversationId]-messages',
		});

		const chatArray = rawChat.items;
		return chatArray.map(
			(message): ChatMessage =>
				// Must be converted into StreamResponse
				isHumanMessage(message) ? message : toStreamResponse(message),
		);
	}

	public async deleteChat(conversationId: string, controller?: AbortController): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers: headers,
			signal: controller?.signal,
		};
		await this.fetch({
			path: `/rovo/v1/chat/conversation/${conversationId}`,
			options,
			holdName: 'rovo-v1-chat-conversation-[conversationId]',
		});
	}

	public async getAgents(controller?: AbortController): Promise<Agent[]> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers: headers,
			signal: controller?.signal,
		};
		const response = await this.fetchJson<{
			items: Agent[];
		}>({
			path: '/rovo/v1/agents',
			options,
			holdName: 'rovo-v1-agents',
		});

		return response.items;
	}

	public async getAgentDetails(id: string, controller?: AbortController): Promise<AgentDetails> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers: headers,
			signal: controller?.signal,
		};
		return this.fetchJson<AgentDetails>({
			path: `/rovo/v1/agents/${id}`,
			options,
			holdName: 'rovo-v1-agents-[id]',
		});
	}

	public async getAgentDetailsByIdentityAccountId(
		identityAccountId: string,
		controller?: AbortController,
	): Promise<AgentDetails> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers: headers,
			signal: controller?.signal,
		};
		return this.fetchJson<AgentDetails>({
			path: `/rovo/v1/agents/accountid/${identityAccountId}`,
			options,
			holdName: 'rovo-v1-agents-accountid-[identityAccountId]',
		});
	}

	public async createAgent(
		payload: PostAgentPayload,
		controller?: AbortController,
	): Promise<Agent> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers: headers,
			body: JSON.stringify(payload),
			signal: controller?.signal,
		};
		return this.fetchJson<Agent>({
			path: '/rovo/v1/agents',
			options,
			holdName: 'rovo-v1-agents',
		});
	}

	public async updateAgent(
		id: string,
		payload: PostAgentPayload,
		controller?: AbortController,
	): Promise<Agent> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'PUT',
			headers: headers,
			body: JSON.stringify(payload),
			signal: controller?.signal,
		};
		const response = await this.fetchJson<Agent>({
			path: `/rovo/v1/agents/${id}`,
			options,
			holdName: 'rovo-v1-agents-[id]',
		});

		return response;
	}

	public async deleteAgent(id: string, controller?: AbortController): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers: headers,
			signal: controller?.signal,
		};
		return this.fetchJson<void>({
			path: `/rovo/v1/agents/${id}`,
			options,
			holdName: 'rovo-v1-agents-[id]',
		});
	}

	public async favouriteAgent(id: string, controller?: AbortController): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'POST',
			headers: headers,
			signal: controller?.signal,
		};
		this.fetch({
			path: `/rovo/v1/agents/${id}/favourite`,
			options,
			holdName: 'rovo-v1-agents-[id]-favourite',
		});
	}

	public async unfavouriteAgent(id: string, controller?: AbortController): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers: headers,
			signal: controller?.signal,
		};
		this.fetch({
			path: `/rovo/v1/agents/${id}/favourite`,
			options,
			holdName: 'rovo-v1-agents-[id]-favourite',
		});
	}

	public async createConversationChannel(options?: { name?: string; dialogues?: Dialogues[] }) {
		return this.fetchJson<ConversationChannel & Required<Pick<ConversationChannel, 'name'>>>({
			path: `/rovo/v1/chat/conversation`,
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify({
					name: options?.name ?? generateConversationName(),
					dialogues: options?.dialogues ?? undefined,
				}),
			},
			holdName: 'rovo-v1-chat-conversation',
		});
	}

	public async getConversationChannels(): Promise<ConversationChannel[]> {
		// Clean up `ConversationChannel[] | { items: ConversationChannel[] }` when fg('rovo_convo_ai_migration') is removed
		const result = await this.fetchJson<{
			items: ConversationChannel[];
		}>({
			path: `/rovo/v1/chat/conversations`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-chat-conversations',
		});

		return result.items;
	}

	public updateConversationChannel(id: string, payload: Partial<ConversationChannel>) {
		return this.fetchJson<ConversationChannel>({
			path: `/rovo/v1/chat/conversation/${id}`,
			options: {
				method: 'PUT',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify(payload),
			},
			holdName: 'rovo-v1-chat-conversation-[id]',
		});
	}

	public async resolveConversationAction(id: string, payload: MessageActionPayload) {
		return this.fetchJson<MessageActionResponse>({
			path: `/rovo/v1/chat/conversation/${id}/action`,
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify({
					...payload,
					context: {
						...payload.context,
						browser_url: window.location.href,
					},
				}),
			},
			holdName: 'rovo-v1-chat-conversation-[id]-action',
		});
	}

	// REST starters, newer API
	public async generateAgentStarters(payload: {
		agent_name?: string;
		description?: string;
		system_prompt?: string;
	}): Promise<ConversationStarterResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<ConversationStarterResponse>({
			path: '/rovo/v1/conversation-starters/agent-starter-suggestion',
			options,
			holdName: 'rovo-v1-conversation-starters-agent-starter-suggestion',
		});
	}

	// REST conversation starters, newer API
	public async generateContextualStarters(payload: {
		browser_url: string;
	}): Promise<ConversationStarterResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<ConversationStarterResponse>({
			path: '/rovo/v1/conversation-starters/page-contextual',
			options,
			holdName: 'rovo-v1-conversation-starters-page-contextual',
		});
	}

	// REST dynamic conversation starters (new endpoint)
	public async getDynamicConvoStartersNew(
		payload: DynamicConversationStarterRequestNew,
	): Promise<DynamicConversationStarterResponseNew> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
			'x-experience-id': 'convo-starter-for-rovo-button',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};

		return this.fetchJson<DynamicConversationStarterResponseNew>({
			path: '/api/ai/v2/ai-feature/convo-starters',
			options,
			holdName: 'api-ai-v2-ai-feature-convo-starters',
		});
	}

	public async getWebSearchConfiguration(): Promise<WebSearchConfigurationResponse> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers,
		};
		const result = await this.fetchJson<WebSearchConfigurationResponse>({
			path: '/api/v2/configuration/web-search-ai',
			options,
			holdName: 'api-v2-configuration-web-search-ai',
		});

		return result;
	}

	public async getPrompts(): Promise<GetAllPromptsResponse> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers,
		};
		const result = await this.fetchJson<GetAllPromptsResponse>({
			path: '/api/v2/prompts',
			options,
			holdName: 'api-v2-prompts',
		});

		return result;
	}

	public async getPrompt(promptId: string): Promise<GetPromptResponse> {
		const headers = this.createHeaders();
		const options = {
			method: 'GET',
			headers,
		};
		return this.fetchJson<GetPromptResponse>({
			path: `/api/v2/prompts/${promptId}`,
			options,
			holdName: 'api-v2-prompts-[promptId]',
		});
	}

	public async createPrompt(payload: CreatePromptRequest): Promise<GetPromptResponse> {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});
		const options = {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
		};
		return this.fetchJson<GetPromptResponse>({
			path: '/api/v2/prompts',
			options,
			holdName: 'api-v2-prompts',
		});
	}

	public async deletePrompt(shareId: string): Promise<void> {
		const headers = this.createHeaders();
		const options = {
			method: 'DELETE',
			headers,
		};
		await this.fetch({
			path: `/api/v2/prompts/${shareId}`,
			options,
			holdName: 'api-v2-prompts-delete',
		});
	}

	public getAgentActions = async (): Promise<AgentAction[]> => {
		const response = await this.fetchJson<{
			actions: AgentAction[];
		}>({
			path: '/rovo/v1/agents/configuration/actions',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agents-configuration-actions',
		});

		return response.actions;
	};

	public getAgentKnowledgeConfiguration = (): Promise<AgentKnowledgeConfigurationResponse> => {
		return this.fetchJson<AgentKnowledgeConfigurationResponse>({
			path: `/rovo/v1/agents/configuration/knowledge`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agents-configuration-knowledge',
		});
	};

	public getAgentPermissions = (
		agentId: string,
		payload: GetAgentPermissionsPayload,
	): Promise<AgentPermissionResponse> => {
		return this.fetchJson({
			path: `/rovo/v2/permissions/agents/${agentId}`,
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify(payload),
			},
			holdName: 'rovo-v2-permissions-agents-[agentId]',
		});
	};

	public getRecommendedAgents = ({
		limit,
		query,
		pageUrl,
	}: {
		limit: number;
		query: string; // deprecated: to be removed in QRA-485
		pageUrl?: string;
	}): Promise<{ agents: Agent[]; metadata: { recommendationKey: string } }> => {
		const urlParams = new URLSearchParams({
			limit: limit.toString(),
			query: query,
		});

		if (pageUrl) {
			urlParams.append('pageUrl', pageUrl);
		}

		const fullPath = `/rovo/v1/agents/recommend/v2?${urlParams.toString()}`;

		return this.fetchJson({
			path: fullPath,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agents-recommend-v2',
		});
	};

	public getConversationDebugLogs = async (conversationId: string): Promise<Chat> => {
		const response = await this.fetchJson<{
			items: Chat;
		}>({
			path: `/rovo/v1/chat/conversation/${conversationId}/debug-logs`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-chat-conversation-[conversationId]-debug-logs',
		});

		return response.items;
	};

	public validateFiles = async (
		payload: FileValidationPayload,
	): Promise<FileValidationResponse> => {
		const headers = this.createHeaders({
			'content-type': 'application/json;charset=UTF-8',
		});

		return this.fetchJson<FileValidationResponse>({
			path: `/rovo/v1/chat/message/file/validate`,
			options: {
				method: 'POST',
				headers,
				body: JSON.stringify(payload),
			},
			holdName: 'rovo-v1-chat-message-file-validate',
		});
	};

	public uploadFiles = async (payload: { files: File[] }): Promise<FileUploadResponse> => {
		const formData = new FormData();

		for (const file of payload.files) {
			formData.append('files', file);
		}

		const response = await this.fetchJson<FileUploadResponse>({
			path: `/rovo/v1/chat/message/file`,
			options: {
				method: 'POST',
				headers: this.createHeaders(),
				body: formData,
			},
			holdName: 'rovo-v1-chat-message-file',
		});

		return response;
	};

	public rovoLabsSendMessage = (payload: RovoLabsChatStreamPayload) => {
		return this.fetchStream<StreamMessage>({
			path: '/rovo/v1/agent-playground/chat/stream',
			options: {
				method: 'POST',
				headers: this.createHeaders({
					'content-type': 'application/json;charset=UTF-8',
				}),
				body: JSON.stringify(payload),
			},
			holdName: 'rovo-v1-agent-playground-chat-stream',
		});
	};

	public rovoLabsGetConversations = (): Promise<{ items: ConversationChannel[] }> => {
		return this.fetchJson({
			path: '/rovo/v1/agent-playground/chat/conversations',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agent-playground-chat-conversations',
		});
	};

	public rovoLabsGetTools = (): Promise<{ tools: RovoLabsTool[] }> => {
		return this.fetchJson({
			path: '/rovo/v1/agent-playground/tools',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agent-playground-tools',
		});
	};

	public rovoLabsGetLanguageModels = (): Promise<{ languageModels: RovoLabsLanguageModel[] }> => {
		return this.fetchJson({
			path: '/rovo/v1/agent-playground/language-models',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agent-playground-language-models',
		});
	};

	public rovoLabsGetPrompts = (): Promise<RovoLabsDefaultPrompt> => {
		return this.fetchJson({
			path: '/rovo/v1/agent-playground/prompts',
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-agent-playground-prompts',
		});
	};

	public getGeneratedContent = (payload?: {
		actions?: string[];
		statuses?: string[];
		limit?: number;
	}): Promise<ConversationContent[]> => {
		const queryParams: string[] = [];
		if (payload?.actions && payload.actions.length > 0) {
			queryParams.push(`actionKeys=${payload.actions.join(',')}`);
		}
		if (payload?.statuses && payload.statuses.length > 0) {
			queryParams.push(`statuses=${payload.statuses.join(',')}`);
		}
		if (payload?.limit !== undefined) {
			queryParams.push(`limit=${payload.limit}`);
		}
		const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
		return this.fetchJson({
			path: `/rovo/v1/chat/generated-content${queryString}`,
			options: {
				method: 'GET',
				headers: this.createHeaders(),
			},
			holdName: 'rovo-v1-chat-generated-content',
		});
	};

	public getRovoUserSettings = async (): Promise<RovoUserSettings> => {
		return this.fetchJson<RovoUserSettings>({
			path: '/rovo/v1/chat/user-settings',
			options: {
				method: 'GET',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
			},
			holdName: 'rovo-v1-chat-user-settings',
		});
	};

	public updateRovoUserSettings = async (
		settings: Partial<RovoUserSettings>,
	): Promise<RovoUserSettings> => {
		return this.fetchJson<RovoUserSettings>({
			path: '/rovo/v1/chat/user-settings',
			options: {
				method: 'PUT',
				headers: this.createHeaders({
					'Content-Type': 'application/json',
				}),
				body: JSON.stringify(settings),
			},
			holdName: 'rovo-v1-chat-user-settings',
		});
	};
}

const AssistanceServiceContext = createContext<AssistanceService>(
	new AssistanceServiceImpl({
		baseUrl: '',
		product: '',
		experienceId: '',
	}),
);

export const AssistanceServiceProvider = ({
	value,
	children,
}: {
	value: AssistanceService;
	children: ReactNode;
}): React.JSX.Element => (
	<AssistanceServiceContext.Provider value={value}>{children}</AssistanceServiceContext.Provider>
);

export type UseAssistanceServiceParams = {
	/** If a parent AssistanceServiceProvider is not rendered, this field MUST be provided in order for agents to work */
	product?: AssistanceServiceProduct;
	/** Defaults to `ai-mate` */
	experienceId?: string;
	headers?: Record<string, string> | undefined;
};

/**
 * If these props are provided and one of them are populated, they will take precedence over the `AssistanceServiceProvider` with the missing props filled in
 *
 * @deprecated Please use factory function `createHookWithAssistanceService` instead. We had problems where the consumer of this hook are missing the parent context or not passing in all required paramters to call convo-ai. {@link https://hello.atlassian.net/wiki/spaces/AA6/pages/4323250987/RFC-004+Rovo+components+missing+headers+if+called+outside+chat}
 **/
export const useAssistanceService = (props?: UseAssistanceServiceParams) => {
	di(useContext, AssistanceServiceImpl);

	const service = useContext(AssistanceServiceContext);

	setRovoChatProduct(props?.product ?? service.getConfig().product);

	if (!props || (!props.experienceId && !props.product && !props.headers)) {
		return service;
	}

	const product = props.product ?? service.getConfig().product;
	const experienceId = props.experienceId ?? service.getConfig().experienceId;
	const headers = props.headers ?? service.getConfig().headers;

	return new AssistanceServiceImpl({
		...service.getConfig(),
		product,
		experienceId,
		headers,
	});
};

type CustomHeaders = Record<Lowercase<string>, string> | undefined;

export type AssistanceServiceInputParam = {
	assistanceServiceParams: {
		/**
		 * This parameter is used as tracing in instrumentation
		 * pass a string that defines where the hook is being called from
		 * make it as granular as possible per touchpoint
		 */
		touchpointSource: string;
		product: AssistanceServiceProduct;
		experienceId: string;
		siteId: string;
		headers?: CustomHeaders;
		baseUrl?: string;
		minionAlias?: string;
	};
};

export type AssistanceServiceInjectedParams = {
	assistanceServiceInjectedParams: {
		experienceTracker: ExperienceTrackerAPI;
		assistanceService: AssistanceService;
		touchpointSource: string;
		product: AssistanceServiceProduct;
		experienceId: string;
		siteId: string;
		headers?: CustomHeaders;
		baseUrl?: string;
	};
};

/**
 * Created for https://hello.atlassian.net/wiki/spaces/AA6/pages/4323250987/RFC-004+Rovo+components+missing+headers+if+called+outside+chat
 * many hooks that require `assistanceService`, often are missing parameters that causes convo-ai call to be 400
 * this hook factory is created to ensure that the `assistanceService` is always called with the correct parameters in any of those hooks
 *
 * Input: react hook that will receive extra prop `assistanceServiceInjectedParams`
 * Output: react hook that enforce `assistanceServiceParams` as a required parameter
 *
 * e.g.
 * const useSomething = createHookWithAssistanceService(
 * 	(hookParams: { customProp: string } & AssistanceServiceInjectedParams) => {
 * 		// this inner hook will get `assistanceServiceInjectedParams` as a prop
 * 		const { customProp, assistanceServiceInjectedParams: { assistanceService } } = hookParams;
 * 		return { ... };
 * 	}
 * );
 *
 * now `useSomething` will have `assistanceServiceParams`, as a required param
 * e.g.
 *
 * useSomething({
 * 	customProp: 'value',
 * 	assistanceServiceParams: { product: 'confluence', experienceId: 'ai-mate', ... }
 * });
 */
export const createHookWithAssistanceService = <P extends AssistanceServiceInjectedParams, R>(
	originalHook: (props: P) => R,
) => {
	return (props: Omit<P, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam): R => {
		di(AssistanceServiceImpl, useAIMateExperienceTracker);
		const { assistanceServiceParams, ...restProps } = props;

		const experienceTracker = useAIMateExperienceTracker({
			touchpointSource: assistanceServiceParams.touchpointSource,
		});

		const headers: Record<string, string> = useMemo(() => {
			// even though AssistanceServiceInputParam enforces lowercase header keys,
			// it is possible to get a non lowercase key if a prebuilt object was assigned to headers at runtime,
			// so this is an additional safety net, without this duplicate headers keys with different case
			// can get appended during header normalization causing request to fail
			// Relevant thread: https://atlassian.slack.com/archives/C0677M19GU9/p1748531977923069?thread_ts=1747336061.856749&cid=C0677M19GU9
			const inputHeaders = props.assistanceServiceParams.headers
				? Object.fromEntries(
						Object.entries(props.assistanceServiceParams.headers).map(([key, value]) => [
							key.toLowerCase(),
							value,
						]),
					)
				: undefined;
			return {
				'x-cloudid': props.assistanceServiceParams.siteId,
				...inputHeaders,
			};
		}, [props.assistanceServiceParams.siteId, props.assistanceServiceParams.headers]);

		const assistanceService = useMemo(() => {
			return new AssistanceServiceImpl({
				product: assistanceServiceParams.product,
				experienceId: assistanceServiceParams.experienceId,
				headers,
				baseUrl: assistanceServiceParams.baseUrl,
				minionAlias: assistanceServiceParams.minionAlias,
			});
		}, [
			assistanceServiceParams.baseUrl,
			assistanceServiceParams.experienceId,
			assistanceServiceParams.product,
			headers,
			assistanceServiceParams.minionAlias,
		]);

		const result = originalHook({
			...restProps,
			assistanceServiceInjectedParams: {
				...assistanceServiceParams,
				headers,
				experienceTracker,
				assistanceService,
			},
		} as unknown as P);

		return result;
	};
};
